import { useTenant } from "@/stores/tenantStore.tsx";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button.tsx";
import { useAuth } from "@/hooks/useAuth.ts";
import type { ApiError } from "@/api/client.ts";
import { SEOHead } from "@/components/seo/SEOHead.tsx";

export function LoginPage() {
  const { siteName } = useTenant();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const error = login.error as ApiError | null;

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  return (
    <>
      <SEOHead title="Log in" description="Log in to your TubeForge account." path="/login" noindex={true} />
      {/* Mobile logo (hidden on desktop where branding panel shows) */}
      <div className="lg:hidden mb-8 text-center">
        <span className="text-2xl font-bold text-surface-900">{siteName}</span>
      </div>

      <h1 className="text-2xl font-semibold text-surface-900 mb-1">Welcome back</h1>
      <p className="text-[13px] text-surface-400 mb-6">Log in to your account</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" autoComplete="email" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        <div>
          <label className="block text-[12px] font-medium text-surface-700 mb-1.5">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" autoComplete="current-password" required
            className="w-full h-10 px-3 rounded-lg border border-surface-200 bg-white text-[13px] text-surface-900
                       placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
          />
        </div>
        {error && <p className="text-[12px] text-danger">{error.message ?? "Login failed"}</p>}
        <Button type="submit" loading={login.isPending} fullWidth>Log in</Button>
      </form>

      <div className="my-5 flex items-center gap-3">
        <div className="flex-1 h-px bg-surface-200" />
        <span className="text-[12px] text-surface-400">or</span>
        <div className="flex-1 h-px bg-surface-200" />
      </div>

      <Link to="/otp">
        <Button variant="outline" fullWidth>Sign in with email code</Button>
      </Link>

      <p className="mt-6 text-center text-[13px] text-surface-400">
        Don't have an account?{" "}
        <Link to="/signup" className="text-brand-600 hover:underline font-medium">Sign up</Link>
      </p>
      <p className="mt-2 text-center text-[13px]">
        <Link to="/forgot-password" className="text-surface-400 hover:text-surface-600 hover:underline">Forgot password?</Link>
      </p>
    </>
  );
}
